import React, { useCallback, useEffect, useState } from "react";
import { FilterButton, FilterChips, FilterSheet } from "../../components/FilterSheet";
import { startViewAs } from "../../utils/viewAs";
import Modal from "../../components/Modal";
import Pager from "../../components/Pager";
import SecretBox from "../../components/SecretBox";
import Stat from "../../components/Stat";
import { errorMessage } from "../../services";
import { createFranchisee, deleteFranchisee, getFranchisees, updateFranchisee } from "../../services/franchisees";
import { money } from "../../utils/format";
import { useDebounced } from "../../utils/hooks";
import DataTable from "../../components/DataTable";
import Loader, { Spinner } from "../../components/Loader";

// Deleting a partner keeps their customers and websites running (they move to Ownezzy direct) and removes only the
// partner's login and unused licences, so the name must be typed to confirm; the server checks it again.
function DeletePartnerModal({ partner, notify, onClose, onDeleted }) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const matches = typed.trim().toLowerCase() === partner.name.trim().toLowerCase();

  const remove = async (e) => {
    e.preventDefault();
    if (!matches) return;
    setBusy(true);
    try {
      const { data } = await deleteFranchisee(partner.id, typed);
      notify(data.message);
      onDeleted();
    } catch (err) {
      notify(errorMessage(err), "error");
      setBusy(false);
    }
  };

  return (
    <Modal eyebrow="DELETE PARTNER" title={`Delete ${partner.name}?`} onClose={onClose}>
      <form onSubmit={remove}>
        <div className="delete-site-warning">
          <span aria-hidden="true">⚠</span>
          <div>
            <b>This cannot be undone.</b>
            <ul>
              <li>Their partner login is deleted and they are signed out at once</li>
              <li>{partner.storesAllocated ? `Their ${partner.storesAllocated} website${partner.storesAllocated === 1 ? "" : "s"} and those customers stay, and move to Ownezzy direct` : "They have no websites"}</li>
              {partner.licencesRemaining > 0 && <li>Their {partner.licencesRemaining} unused licence{partner.licencesRemaining === 1 ? " is" : "s are"} lost</li>}
            </ul>
            <p className="muted">Their payments are kept in Payments.</p>
          </div>
        </div>
        <label className="admin-field">Type <b>{partner.name}</b> to confirm
          <input value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" autoFocus />
        </label>
        <div className="product-modal-actions">
          <button type="button" className="outline-btn" onClick={onClose}>Cancel</button>
          <button className="danger-btn" disabled={!matches || busy}>{busy ? <><Spinner /> Deleting</> : "Delete partner"}</button>
        </div>
      </form>
    </Modal>
  );
}

const PERMISSIONS = [
  ["content", "Edit websites"],
  ["orders", "Manage orders"],
  ["customers", "View shoppers & reviews"],
];

function FranchiseeModal({ franchisee, onClose, onSaved, notify }) {
  const editing = Boolean(franchisee);
  const [form, setForm] = useState({
    name: franchisee ? franchisee.name : "",
    email: franchisee ? franchisee.email || "" : "",
    phone: franchisee ? franchisee.phone || "" : "",
    permissions: franchisee ? franchisee.permissions : ["content", "orders"],
    websiteAllowance: franchisee ? String(franchisee.websiteAllowance ?? 0) : "0",
    commissionPercent: franchisee ? String(franchisee.commissionPercent ?? 25) : "25",
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const togglePermission = (key) =>
    setForm((f) => ({ ...f, permissions: f.permissions.includes(key) ? f.permissions.filter((p) => p !== key) : [...f.permissions, key] }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.email.trim() && !form.phone.trim()) return setError("Enter an email or a phone number.");
    setBusy(true);
    try {
      const payload = { name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim(), permissions: form.permissions };
      if (editing) {
        await updateFranchisee(franchisee.id, { ...payload, websiteAllowance: Number(form.websiteAllowance) || 0, commissionPercent: Number(form.commissionPercent) || 0 });
        notify("Franchise partner updated");
        onSaved(null);
      } else {
        const { data } = await createFranchisee(payload);
        onSaved({ title: `Franchise partner created: ${data.franchisee.name}`, login: data.franchisee.email || data.franchisee.phone, password: data.temporaryPassword });
      }
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal eyebrow="PARTNER NETWORK" title={editing ? "Edit franchise partner" : "Add franchise partner"} onClose={onClose}>
      <form onSubmit={submit}>
        {error && <div className="form-error">{error}</div>}
        <label className="admin-field">Name<input required minLength={2} value={form.name} onChange={set("name")} /></label>
        <div className="form-row">
          <label className="admin-field">Email<input type="email" value={form.email} onChange={set("email")} /></label>
          <label className="admin-field">Mobile number<input value={form.phone} onChange={set("phone")} /></label>
        </div>
        {editing && (
          <div className="form-row">
            <label className="admin-field">Free trial websites<input type="number" min="0" max="10000" value={form.websiteAllowance} onChange={set("websiteAllowance")} /><small className="field-help">{franchisee.allowanceUsed || 0} used. Websites they can create without buying licences.</small></label>
            <label className="admin-field">Commission %<input type="number" min="0" max="100" step="0.5" value={form.commissionPercent} onChange={set("commissionPercent")} /><small className="field-help">Share of their customers' paid plans.</small></label>
          </div>
        )}
        <div className="admin-field">What this partner may do inside their own websites</div>
        <div className="check-row">
          {PERMISSIONS.map(([key, label]) => (
            <label key={key}><input type="checkbox" checked={form.permissions.includes(key)} onChange={() => togglePermission(key)} />{label}</label>
          ))}
        </div>
        <div className="product-modal-actions">
          <button type="button" className="outline-btn" onClick={onClose}>Cancel</button>
          <button className="primary-btn" disabled={busy}>{busy ? <><Spinner /> Saving</> : editing ? "Save changes" : "Create partner"}</button>
        </div>
      </form>
    </Modal>
  );
}

// Franchise partners (Super Admin): add or edit partners and the permissions they get
const STATUS_OPTIONS = [
  { value: "", label: "All" },
  { value: "active", label: "Active", tone: "green" },
  { value: "inactive", label: "Inactive", tone: "amber" },
  { value: "suspended", label: "Suspended", tone: "red" },
];

export default function FranchiseesTab({ notify, setTab }) {
  const [partners, setPartners] = useState([]);
  const [stats, setStats] = useState(null);
  const [meta, setMeta] = useState({ total: 0, page: 1, pages: 1 });
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modal, setModal] = useState(null);
  const [secret, setSecret] = useState(null);
  const [deleting, setDeleting] = useState(null); // the partner being deleted
  const debouncedSearch = useDebounced(search);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getFranchisees({ search: debouncedSearch, status, page: pageNumber, limit: 20 });
      setPartners(data.franchisees);
      setStats(data.stats);
      setMeta({ total: data.total, page: data.page, pages: data.pages });
      setError("");
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, status, pageNumber]);

  useEffect(() => { load(); }, [load]);

  const toggleStatus = async (partner) => {
    const next = partner.status === "active" ? "suspended" : "active";
    try {
      await updateFranchisee(partner.id, { status: next });
      notify(`${partner.name} ${next === "active" ? "activated" : "suspended"}`);
      load();
    } catch (err) {
      notify(errorMessage(err), "error");
    }
  };

  return (
    <>
      <div className="page-heading">
        <div>
          <p className="eyebrow">PARTNER NETWORK</p>
          <h1>Franchisees</h1>
          <p className="subheading">Manage sub-admin partners and their bulk website allocation.</p>
        </div>
        <button className="primary-btn" onClick={() => setModal("new")}>＋ Add franchisee</button>
      </div>

      {secret && <SecretBox {...secret} onDone={() => setSecret(null)} />}
      {error && <div className="admin-error">{error}</div>}

      {stats && (
        <div className="stats-grid">
          <Stat label="Franchise partners" value={stats.totalPartners} meta="Registered partners" icon="♧" />
          <Stat label="Websites allocated" value={stats.websitesAllocated} meta="Across partner accounts" icon="▤" />
          <Stat label="Minimum order" value={stats.minimumOrder} meta="Websites per purchase" icon="▦" />
          <Stat label="Partner rate" value={money(stats.partnerRate)} meta="Per website / month" icon="₹" />
        </div>
      )}

      <div className="panel table-panel">
        <div className="table-toolbar">
          <input value={search} onChange={(e) => { setSearch(e.target.value); setPageNumber(1); }} placeholder="Search partner name, email or phone…" />
          <FilterButton count={status ? 1 : 0} onClick={() => setFiltersOpen(true)} />
          <div className="toolbar-inline-filters">
            <select value={status} onChange={(e) => { setStatus(e.target.value); setPageNumber(1); }} aria-label="Filter by status">
              <option value="">All statuses</option><option value="active">Active</option><option value="inactive">Inactive</option><option value="suspended">Suspended</option>
            </select>
          </div>
          <span>{meta.total} partners</span>
        </div>
        <div className="toolbar-count">{meta.total} partners</div>
        <DataTable>
            <thead><tr><th>PARTNER</th><th>WEBSITES</th><th>LICENCES LEFT</th><th>FREE WEBSITES</th><th>COMMISSION</th><th>MAY DO</th><th>STATUS</th><th>ACTIONS</th></tr></thead>
            <tbody>
              {partners.map((p) => (
                <tr key={p.id}>
                  <td><div className="site-name-cell"><span className="site-mark">{p.name.slice(0, 1)}</span><span><b>{p.name}</b><small>{p.email || p.phone}</small></span></div></td>
                  <td>{p.storesAllocated}</td>
                  <td>{p.licencesRemaining}</td>
                  <td>{p.allowanceUsed} / {p.websiteAllowance || 0}{p.selfRegistered && <small className="muted-note"> · signed up</small>}</td>
                  <td>{p.commissionPercent ?? 25}%</td>
                  <td>{p.permissions.length ? p.permissions.join(", ") : "—"}</td>
                  <td><span className={`status-pill ${p.status === "active" ? "status-on" : "status-off"}`}><i />{p.status}</span></td>
                  <td>
                    <div className="row-actions">
                      <button className="edit-action" onClick={() => setModal(p)}>Edit</button>
                      <button className="toggle-action" onClick={() => toggleStatus(p)}>{p.status === "active" ? "Suspend" : "Activate"}</button>
                      {p.status === "active" && <button className="view-as-action" onClick={() => startViewAs(p.id).catch((err) => notify(errorMessage(err), "error"))}>👁 View as</button>}
                      <button className="delete-action" onClick={() => setDeleting(p)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
              {!partners.length && <tr><td colSpan="8" className="empty-row">{loading ? <Loader label="Loading partners" /> : "No franchise partners yet."}</td></tr>}
            </tbody>
        </DataTable>
        <Pager page={meta.page} pages={meta.pages} total={meta.total} noun="partners" onPage={setPageNumber} />
      </div>

      {stats && (
        <div className="panel info-panel tab-gap">
          <h2>Franchise pricing rules</h2>
          <p>Sub Admin accounts purchase website licences in bulk. Each order must contain at least {stats.minimumOrder} websites and is charged at {money(stats.partnerRate)} per website per month. Purchases stay pending until you confirm the payment.</p>
          <button className="outline-btn" onClick={() => setTab("Payments")}>Review partner billing →</button>
        </div>
      )}

      {filtersOpen && (
        <FilterSheet title="Filter franchisees" resultLabel={`Show ${meta.total} partner${meta.total === 1 ? "" : "s"}`} onClose={() => setFiltersOpen(false)} onReset={() => { setStatus(""); setPageNumber(1); }}>
          <FilterChips label="Status" value={status} onChange={(v) => { setStatus(v); setPageNumber(1); }} options={STATUS_OPTIONS} />
        </FilterSheet>
      )}
      {deleting && <DeletePartnerModal partner={deleting} notify={notify} onClose={() => setDeleting(null)} onDeleted={() => { setDeleting(null); load(); }} />}
      {modal && (
        <FranchiseeModal franchisee={modal === "new" ? null : modal} notify={notify} onClose={() => setModal(null)}
          onSaved={(created) => { setModal(null); if (created) setSecret(created); load(); }} />
      )}
    </>
  );
}
